import type { FunctionComponent, ReactNode } from 'react';

import { Input } from '@dr.pogodin/react-utils';

import Table from 'components/Table';

import { AdminPage } from 'components/admin';
import AddNewButton from 'components/admin/AddNewButton';
import MessageModal, { type IconT } from 'components/admin/MessageModal';
import { useAdminUsers } from 'hooks/useAdminUsers';

import { P_ADMIN_ADD_NEW_ADMIN_USER, useGlobalState } from 'utils';

import IconSearch from 'assets/images/icon-search-24px.svg';

import Item from './Item';

import inputTheme from './input.scss';

import S from './style.scss';

const AdminAdminUsersPage: FunctionComponent = () => {
  const adminUsers = useAdminUsers();

  const items: ReactNode[] = [];

  if (adminUsers.data) {
    for (const user of adminUsers.data) {
      items.push(<Item key={user.id} user={user} />);
    }
  }

  const [modal, setModal] = useGlobalState('ui.addNewAdmin.modal');

  let icon: IconT | undefined;
  let modalContent: ReactNode;

  switch (modal) {
    case 'CREATED_NEW':
      icon = 'OK';
      modalContent = (
        <>
          <h1>New admin created!</h1>
          <p>An invitation has been sent.</p>
        </>
      );
      break;
    case 'ERROR':
      icon = 'FAIL';
      modalContent = (
        <>
          <h1>Failed to create admin!</h1>
          <p>Failed to create admin.</p>
        </>
      );
      break;
    case 'PROMOTED_EXISTING':
      icon = 'OK';
      modalContent = (
        <>
          <h1>User promoted to admin!</h1>
          <p>An notification email has been sent.</p>
        </>
      );
      break;
    case undefined: break;
    default: throw Error('Unexpected case');
  }

  return (
    <AdminPage
      contentClassName={S.container}
      subTitle="Manage admin roles and users"
      title="Admin Users"
    >
      <div styleName="header">
        <div styleName="searchWrapper">
          <Input placeholder="Search" theme={inputTheme}>
            <IconSearch styleName="searchIcon" />
          </Input>
        </div>
        <div styleName="filler" />
        <div styleName="actions">
          <AddNewButton
            hideLabelOnMobile
            label="Add New Admin"
            to={P_ADMIN_ADD_NEW_ADMIN_USER}
          />
        </div>
      </div>
      {icon ? (
        <MessageModal
          cancelButtonLabel="Understood"
          icon={icon}
          onCancel={() => {
            setModal(undefined);
          }}
        >
          {modalContent}
        </MessageModal>
      ) : null}
      <Table busy={adminUsers.loading}>
        <thead>
          <tr>
            <th>Admin Name</th>
            <th>Admin ID</th>
            <th>Admin Since</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {items}
        </tbody>
      </Table>
    </AdminPage>
  );
};

export default AdminAdminUsersPage;
